import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Slider,
} from "@mui/material";
import {
  MonitorWeight as WeightIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Mood as MoodIcon,
  DirectionsWalk as MobilityIcon,
  Chat as CommunicationIcon,
  Favorite as HealthIcon,
  EmojiEmotions as EmotionalIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditWellbeingModal from "./editWellbeingModal";
import {
  createWellbeing,
  getWellbeingByClientID,
  deleteWellbeingByID,
} from "../../../../../../api/services/observations";

const Wellbeing = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    mobility: 0,
    health: 0,
    communication: 0,
    activity: 0,
    emotional_state: 0,
    date_time: "",
  });
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  const profile = useUserProfile();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSliderChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      mobility: 0,
      health: 0,
      communication: 0,
      activity: 0,
      emotional_state: 0,
      date_time: "",
    });
  };

  const { data: wellbeings, isLoading } = useQuery([QK.wellbeings, clientID], () =>
    getWellbeingByClientID(clientID)
  );

  const createMutation = useMutation(createWellbeing, {
    onSuccess: () => {
      enqueueSnackbar("Wellbeing entry created successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.wellbeings, clientID]);
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(error?.response?.data?.message || "Failed to create wellbeing entry.", {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation(deleteWellbeingByID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.wellbeings, clientID]);
      enqueueSnackbar("Wellbeing entry deleted successfully", { variant: "success" });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete wellbeing entry", { variant: "error" });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedFormData = {
      ...formData,
      client: clientID,
      carer: profile?.id,
    };
    createMutation.mutate(updatedFormData);
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const handleUpdate = (id) => {
    handleOpenModal(id);
  };

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (RowID) {
      deleteMutation.mutate(RowID);
    }
  };

  const headers = [
    { key: "date_time", label: "Date & Time" },
    { key: "health", label: "Health" },
    { key: "mobility", label: "Mobility" },
    { key: "emotional_state", label: "Emotional" },
    { key: "communication", label: "Communication" },
    { key: "activity", label: "Activity" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (
      header.key === "health" ||
      header.key === "mobility" ||
      header.key === "emotional_state" ||
      header.key === "communication" ||
      header.key === "activity"
    ) {
      const value = item[header.key] || 0;
      const percentage = Math.round(value * 100);
      let color = "#EF4444";
      if (percentage >= 70) color = "#10B981";
      else if (percentage >= 40) color = "#F59E0B";
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 60,
              height: 4,
              borderRadius: 2,
              bgcolor: "#F2F4F7",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${percentage}%`,
                height: "100%",
                bgcolor: color,
                borderRadius: 2,
                transition: "width 0.3s",
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: "#667085", fontWeight: 500, minWidth: 30 }}>
            {percentage}%
          </Typography>
        </Box>
      );
    }
    return item[header.key] || "-";
  };

  const sliderFields = [
    {
      key: "mobility",
      label: "Mobility",
      icon: <MobilityIcon sx={{ color: "#2469F0", fontSize: 16 }} />,
      accent: "#2469F0",
    },
    {
      key: "health",
      label: "Health",
      icon: <HealthIcon sx={{ color: "#10B981", fontSize: 16 }} />,
      accent: "#10B981",
    },
    {
      key: "communication",
      label: "Communication",
      icon: <CommunicationIcon sx={{ color: "#F59E0B", fontSize: 16 }} />,
      accent: "#F59E0B",
    },
    {
      key: "activity",
      label: "Activity",
      icon: <MoodIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />,
      accent: "#8B5CF6",
    },
    {
      key: "emotional_state",
      label: "Emotional",
      icon: <EmotionalIcon sx={{ color: "#EF4444", fontSize: 16 }} />,
      accent: "#EF4444",
    },
  ];

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 200px",
    minWidth: 0,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "14px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #EAECF0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WeightIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#101828",
                  letterSpacing: "-0.01em",
                }}
              >
                Add Wellbeing Entry
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
                Record daily wellbeing observations
              </Typography>
            </Box>
          </Box>
          <Chip
            label="New"
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: "#ECFDF3",
              color: "#027A48",
              borderRadius: "6px",
              border: "1px solid #A6F4C5",
            }}
          />
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="wellbeing-form" style={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                width: "100%",
                mb: 2,
              }}
            >
              {sliderFields.map((field) => {
                const value = formData[field.key] || 0;
                const percentage = Math.round(value * 100);
                return (
                  <Box
                    key={field.key}
                    sx={{
                      ...cardSx,
                      "&:hover": {
                        borderColor: alpha(field.accent, 0.4),
                        boxShadow: `0px 2px 8px ${alpha(field.accent, 0.08)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "7px",
                          bgcolor: alpha(field.accent, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {field.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#344054",
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: field.accent,
                          ml: "auto",
                        }}
                      >
                        {percentage}%
                      </Typography>
                    </Box>
                    <Box sx={{ mt: "auto" }}>
                      <Slider
                        value={value}
                        onChange={(_, v) => handleSliderChange(field.key, v)}
                        min={0}
                        max={0.8}
                        step={0.2}
                        marks={[
                          { value: 0, label: "0" },
                          { value: 0.2, label: "0.2" },
                          { value: 0.4, label: "0.4" },
                          { value: 0.6, label: "0.6" },
                          { value: 0.8, label: "0.8" },
                        ]}
                        sx={{
                          color: field.accent,
                          py: 0.5,
                          "& .MuiSlider-thumb": {
                            width: 14,
                            height: 14,
                            boxShadow: `0 0 0 4px ${alpha(field.accent, 0.15)}`,
                            "&:hover": {
                              boxShadow: `0 0 0 6px ${alpha(field.accent, 0.2)}`,
                            },
                          },
                          "& .MuiSlider-rail": {
                            bgcolor: "#F2F4F7",
                            opacity: 1,
                          },
                          "& .MuiSlider-mark": {
                            bgcolor: "#D0D5DD",
                            height: 4,
                            width: 4,
                            borderRadius: "50%",
                          },
                          "& .MuiSlider-markLabel": {
                            fontSize: "0.6rem",
                            color: "#98A2B3",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}

              <Box
                sx={{
                  ...cardSx,
                  "&:hover": {
                    borderColor: alpha("#667085", 0.4),
                    boxShadow: `0px 2px 8px ${alpha("#667085", 0.08)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "7px",
                      bgcolor: alpha("#667085", 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CalendarIcon sx={{ color: "#667085", fontSize: 16 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#344054",
                    }}
                  >
                    Date & Time
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#F04438",
                      ml: "auto",
                    }}
                  >
                    Required
                  </Typography>
                </Box>
                <Box sx={{ mt: "auto" }}>
                  <CustomDateField
                    name="date_time"
                    label=""
                    value={formData.date_time || ""}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                pt: 2,
                mt: 1,
                borderTop: "1px solid #EAECF0",
              }}
            >
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={createMutation.isLoading}
                startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  borderColor: "#D0D5DD",
                  color: "#344054",
                  px: 2.5,
                  height: 38,
                  "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={createMutation.isLoading}
                startIcon={
                  createMutation.isLoading ? (
                    <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    <SaveIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  bgcolor: "#2469F0",
                  height: 38,
                  px: 3,
                  boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
                  },
                }}
              >
                {createMutation.isLoading ? "Saving..." : "Save Wellbeing"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Card
        sx={{
          width: "100%",
          borderRadius: "14px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #EAECF0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "#101828",
                letterSpacing: "-0.01em",
              }}
            >
              Wellbeing Records
            </Typography>
            <Chip
              label={wellbeings?.length || 0}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: "#F2F4F7",
                color: "#344054",
                borderRadius: "6px",
                border: "1px solid #EAECF0",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={wellbeings || []}
            headers={headers}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this wellbeing entry?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditWellbeingModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default Wellbeing;